import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useGameStore } from '../store/gameStore';

/** Visible duration of the recap window — must match GameEngine.recap.duration. */
const RECAP_MS = 1500;

/**
 * Death recap overlay — covers the 1.5s window between the player's death
 * and the DeathModal. The canvas is already running the camera cinematic
 * (locked onto the killer's snake) underneath; this component layers the
 * text + a thin progress bar on top.
 *
 * Reads the killer identity + final rank + final mass from gameStore.hud
 * fields populated by the engine. The Skip button calls `skipRecap()`
 * which lives on the engine — the modal pops immediately after.
 *
 * Why this lives outside HUD.tsx: it's a self-contained cinematic
 * presentation tier. Keeping it isolated means the HUD doesn't need to
 * grow per-frame timer state, and the overlay can be swapped or
 * disabled later (e.g. for a "no recap" accessibility setting) without
 * touching the rest of the HUD.
 */
export function DeathRecapOverlay() {
  const recapActive = useGameStore((s) => s.hud.recapActive);
  const killerName = useGameStore((s) => s.hud.recapKillerName);
  const killerHue = useGameStore((s) => s.hud.recapKillerHue);
  const rank = useGameStore((s) => s.hud.recapRank);
  const finalMass = useGameStore((s) => s.hud.recapFinalMass);
  const skipRecap = useGameStore((s) => s.skipRecap);

  // Local progress bar — purely cosmetic. Restarts each time recapActive
  // flips true. The engine owns the actual timer; if engine ends the
  // recap early (killer dies mid-cinematic), the overlay just unmounts.
  const [t, setT] = useState(0);
  useEffect(() => {
    if (!recapActive) return;
    setT(0);
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const elapsed = (now - start) / RECAP_MS;
      setT(Math.min(1, elapsed));
      if (elapsed < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [recapActive]);

  // Enter / Space / Escape all skip the recap straight to the DeathModal.
  // The button copy already says "skip ↵" so Enter is the documented
  // shortcut; Space is the second-most-natural for keyboard players, and
  // Escape mirrors the convention of "dismiss this overlay".
  useEffect(() => {
    if (!recapActive) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') {
        e.preventDefault();
        skipRecap?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [recapActive, skipRecap]);

  if (killerHue == null || !killerName) return null;
  const color = `hsl(${killerHue} 100% 70%)`;

  return (
    <AnimatePresence>
      {recapActive && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.18 } }}
          transition={{ duration: 0.22 }}
          className="absolute inset-0 z-30 pointer-events-none flex flex-col items-center justify-end pb-[20vh]"
        >
          {/* Kicker — small "killed by" label. */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.05 }}
            className="text-[10px] uppercase tracking-[0.35em] text-white/55 mb-1"
          >
            killed by
          </motion.div>

          {/* Killer name — huge, glow, in the rival's canonical color.
              The scale-down + x-shake at the start reads as "impact" —
              the moment of getting hit. After the brief sequence the
              name settles into a steady glow for the rest of the recap. */}
          <motion.div
            initial={{ opacity: 0, scale: 1.25, y: 8, x: 0 }}
            animate={{
              opacity: 1,
              scale: 1,
              y: 0,
              x: [0, -6, 5, -3, 2, 0],
            }}
            transition={{
              opacity: { duration: 0.18, delay: 0.05 },
              scale: { type: 'spring', stiffness: 480, damping: 22, delay: 0.05 },
              y: { type: 'spring', stiffness: 480, damping: 22, delay: 0.05 },
              x: { duration: 0.42, delay: 0.05, ease: 'easeOut' },
            }}
            className="text-5xl md:text-6xl font-bold tracking-[0.05em]"
            style={{
              color,
              textShadow: `0 0 22px ${color}, 0 0 56px ${color}80`,
            }}
          >
            {killerName}
          </motion.div>

          {/* Final stats row. */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.22 }}
            className="mt-3 flex items-center gap-4 text-[11px] uppercase tracking-[0.25em] text-white/60"
          >
            <span>
              rank{' '}
              <span className="text-white font-semibold tabular-nums">
                #{rank}
              </span>
            </span>
            <span className="w-1 h-1 rounded-full bg-white/25" />
            <span>
              final mass{' '}
              <span className="text-white font-semibold tabular-nums">
                {finalMass.toLocaleString()}
              </span>
            </span>
          </motion.div>

          {/* Skip button — pointer-events re-enabled just for this control
              so the rest of the overlay doesn't block click-throughs. */}
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.7 }}
            transition={{ duration: 0.4, delay: 0.5 }}
            onClick={() => skipRecap?.()}
            className="mt-6 pointer-events-auto text-[10px] uppercase tracking-[0.25em] text-white/55 hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/50 rounded px-2 py-1"
          >
            skip ↵
          </motion.button>

          {/* Bottom progress bar — fills as the recap window elapses. */}
          <div
            className="absolute bottom-0 left-0 right-0 h-[2px] bg-white/8"
            aria-hidden
          >
            <div
              className="h-full origin-left transition-[width] duration-100 ease-linear"
              style={{
                width: `${t * 100}%`,
                background: color,
                boxShadow: `0 0 6px ${color}`,
              }}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
